import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import type { GardenRow, LocationRow, PlantRow } from "@garden/types/db";
import { supabase } from "../auth";
import { getGardenById } from "../db/gardens";
import { listLocations } from "../db/locations";
import { listPlants, updatePlant } from "../db/plants";
import { useCurrentGardenId } from "../lib/useCurrentGarden";
import { GardenMap, type GardenMapPin } from "../components/GardenMap";
import { GeoJsonImportModal } from "../components/GeoJsonImportModal";
import { ContainerNameModal } from "../components/ContainerNameModal";
import { AddPlantOnMapModal } from "../components/AddPlantOnMapModal";
import { PinchZoom } from "../components/PinchZoom";
import { parseViewport, mapLocations, computePins, type MappedLocation } from "../lib/mapData";
import { polygonBbox, type Polygon } from "../lib/geo";
import { useIsMobile } from "../lib/useIsMobile";
import "./MapPage.css";

type MapState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "no-garden" }
  | { status: "ready"; garden: GardenRow; locations: LocationRow[]; plants: PlantRow[] };

type Mode = "view" | "edit";

export function MapPage() {
  const isMobile = useIsMobile();
  const [state, setState] = useState<MapState>({ status: "loading" });
  const [mode, setMode] = useState<Mode>("view");
  const [importOpen, setImportOpen] = useState(false);
  const [pendingContainer, setPendingContainer] = useState<{ x: number; y: number } | null>(null);
  const [placingPlant, setPlacingPlant] = useState(false);
  const [pendingPlant, setPendingPlant] = useState<{
    location: MappedLocation;
    polygon: Polygon;
    position: { x: number; y: number };
  } | null>(null);
  const [dropError, setDropError] = useState<string | null>(null);
  const navigate = useNavigate();
  const gardenId = useCurrentGardenId();

  useEffect(() => {
    if (!supabase || !gardenId) return;
    let active = true;
    Promise.all([
      getGardenById(supabase, gardenId),
      listLocations(supabase, gardenId),
      listPlants(supabase, gardenId),
    ])
      .then(([garden, locations, plants]) => {
        if (!active) return;
        if (!garden) {
          setState({ status: "no-garden" });
          return;
        }
        setState({ status: "ready", garden, locations, plants });
      })
      .catch((err) => {
        if (!active) return;
        setState({
          status: "error",
          message: err instanceof Error ? err.message : "Could not load map.",
        });
      });
    return () => {
      active = false;
    };
  }, [gardenId]);

  if (state.status === "loading") {
    return <div className="MapPage__loading">Loading…</div>;
  }

  if (state.status === "error") {
    return <p className="MapPage__error">{state.message}</p>;
  }

  if (state.status === "no-garden") {
    return (
      <div className="MapPage__empty">
        <h1 className="MapPage__empty-title">No garden yet</h1>
        <p className="MapPage__empty-lede">
          Your garden will appear here once you have added your first plant.
        </p>
        <Link to="/plants/new" className="MapPage__empty-cta">
          Add your first plant
        </Link>
      </div>
    );
  }

  const { garden, locations, plants } = state;

  const viewport = parseViewport(garden.viewport);
  const importViewport = viewport ?? { width: 700, height: 660 };
  const mappedLocations = mapLocations(locations);
  const pins = computePins(plants, mappedLocations);
  const pinsWithLocation: GardenMapPin[] = pins.map((p) => {
    const plant = plants.find((pl) => pl.id === p.id);
    return { ...p, locationId: plant?.location_id ?? undefined };
  });

  const bedCount = locations.filter((l) => l.kind === "bed").length;
  const containerCount = locations.filter((l) => l.kind === "container").length;

  const handleLocationCreated = (loc: LocationRow) => {
    setState({ status: "ready", garden, locations: [loc, ...locations], plants });
    setImportOpen(false);
    setPendingContainer(null);
  };

  const handlePlantCreated = (plant: PlantRow) => {
    setState({ status: "ready", garden, locations, plants: [plant, ...plants] });
    setPendingPlant(null);
  };

  const handleCanvasPlace = (point: { x: number; y: number }) => {
    if (placingPlant) {
      // Find the smallest location whose bbox contains the click. Bbox-based
      // (not point-in-polygon) — matches the bbox-clamp drag flow and is good
      // enough for v1; users can re-pick position in the modal afterwards.
      const candidates = mappedLocations
        .filter((loc) => loc.polygon != null)
        .map((loc) => {
          const bbox = polygonBbox(loc.polygon!);
          return { loc, bbox };
        })
        .filter(({ bbox }) =>
          point.x >= bbox.minX && point.x <= bbox.maxX && point.y >= bbox.minY && point.y <= bbox.maxY,
        );
      if (candidates.length === 0) {
        setDropError("Click inside a bed or container to place the plant.");
        return;
      }
      candidates.sort(
        (a, b) =>
          (a.bbox.maxX - a.bbox.minX) * (a.bbox.maxY - a.bbox.minY) -
          (b.bbox.maxX - b.bbox.minX) * (b.bbox.maxY - b.bbox.minY),
      );
      const { loc, bbox } = candidates[0];
      const w = bbox.maxX - bbox.minX || 1;
      const h = bbox.maxY - bbox.minY || 1;
      const nx = Math.max(0, Math.min(1, (point.x - bbox.minX) / w));
      const ny = Math.max(0, Math.min(1, (point.y - bbox.minY) / h));
      setPendingPlant({
        location: loc,
        polygon: loc.polygon!,
        position: { x: Math.round(nx * 1000) / 1000, y: Math.round(ny * 1000) / 1000 },
      });
      setPlacingPlant(false);
      setDropError(null);
      return;
    }
    setPendingContainer({ x: point.x, y: point.y });
  };

  const handlePinDrop = async (plantId: string, point: { x: number; y: number }) => {
    if (!supabase) return;
    const plant = plants.find((p) => p.id === plantId);
    if (!plant || !plant.location_id) return;
    const parent: MappedLocation | undefined = mappedLocations.find((l) => l.id === plant.location_id);
    if (!parent || !parent.polygon) return;
    const bbox = polygonBbox(parent.polygon);
    const w = bbox.maxX - bbox.minX || 1;
    const h = bbox.maxY - bbox.minY || 1;
    const nx = Math.max(0, Math.min(1, (point.x - bbox.minX) / w));
    const ny = Math.max(0, Math.min(1, (point.y - bbox.minY) / h));
    const newPosition = {
      x: Math.round(nx * 1000) / 1000,
      y: Math.round(ny * 1000) / 1000,
    };

    const previous = plant.position;
    const optimistic = plants.map((p) =>
      p.id === plantId ? { ...p, position: newPosition } : p,
    );
    setState({ status: "ready", garden, locations, plants: optimistic });
    setDropError(null);

    try {
      const updated = await updatePlant(supabase, plantId, { position: newPosition });
      setState((curr) =>
        curr.status === "ready"
          ? {
              ...curr,
              plants: curr.plants.map((p) => (p.id === plantId ? updated : p)),
            }
          : curr,
      );
    } catch (err) {
      setState((curr) =>
        curr.status === "ready"
          ? {
              ...curr,
              plants: curr.plants.map((p) => (p.id === plantId ? { ...p, position: previous } : p)),
            }
          : curr,
      );
      setDropError(err instanceof Error ? err.message : "Could not save pin position.");
    }
  };

  return (
    <div className={`MapPage${isMobile ? " MapPage--mobile" : ""}`}>
      <div className="MapPage__toolbar">
        <div className="MapPage__title-block">
          <span className="MapPage__title">{garden.name}</span>
          <span className="MapPage__meta">
            {plants.length} {plants.length === 1 ? "plant" : "plants"} · {bedCount}{" "}
            {bedCount === 1 ? "bed" : "beds"} · {containerCount}{" "}
            {containerCount === 1 ? "container" : "containers"}
          </span>
        </div>
        <div className="MapPage__tools">
          {!isMobile && (
            <>
              <button
                type="button"
                className={`MapPage__tool${mode === "view" ? " MapPage__tool--active" : ""}`}
                onClick={() => setMode("view")}
                aria-pressed={mode === "view"}
              >
                View
              </button>
              <button
                type="button"
                className={`MapPage__tool${mode === "edit" ? " MapPage__tool--active" : ""}`}
                onClick={() => setMode("edit")}
                aria-pressed={mode === "edit"}
              >
                Edit
              </button>
            </>
          )}
          {!isMobile && mode === "edit" && (
            <>
              <button
                type="button"
                className="MapPage__tool MapPage__tool--cta"
                onClick={() => {
                  setPlacingPlant(true);
                  setDropError(null);
                }}
                disabled={placingPlant}
              >
                <svg
                  className="MapPage__tool-icon"
                  viewBox="0 0 14 14"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                >
                  <path d="M3 7h8m-4-4v8" />
                </svg>
                Add plant
              </button>
              <button
                type="button"
                className="MapPage__tool"
                onClick={() => setImportOpen(true)}
              >
                <svg
                  className="MapPage__tool-icon"
                  viewBox="0 0 14 14"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                >
                  <path d="M3 7h8m-4-4v8" />
                </svg>
                Import GeoJSON
              </button>
            </>
          )}
        </div>
      </div>

      {!isMobile && mode === "edit" && !placingPlant && (
        <div className="MapPage__hint">
          Edit mode: click empty area to place a container · drag a plant pin to move it
          · "Add plant" drops a new plant in a bed · "Import GeoJSON" adds a bed.
        </div>
      )}

      {placingPlant && (
        <div className="MapPage__hint MapPage__hint--placing">
          <span>Click inside a bed or container to drop the new plant.</span>
          <button
            type="button"
            className="MapPage__hint-cancel"
            onClick={() => setPlacingPlant(false)}
          >
            Cancel
          </button>
        </div>
      )}

      {dropError && <p className="MapPage__error">{dropError}</p>}

      <div className="MapPage__canvas">
        {isMobile ? (
          <PinchZoom>
            <GardenMap
              garden={{ id: garden.id, viewport }}
              locations={mappedLocations}
              pins={pinsWithLocation}
              mode="view"
              onPinClick={(id) => navigate(`/plants/${id}`, { state: { from: "/map" } })}
            />
          </PinchZoom>
        ) : (
          <GardenMap
            garden={{ id: garden.id, viewport }}
            locations={mappedLocations}
            pins={pinsWithLocation}
            mode={mode}
            onPinClick={(id) => navigate(`/plants/${id}`, { state: { from: "/map" } })}
            onCanvasPlace={handleCanvasPlace}
            onPinDrop={handlePinDrop}
          />
        )}
      </div>

      {importOpen && (
        <GeoJsonImportModal
          gardenId={garden.id}
          viewport={importViewport}
          onClose={() => setImportOpen(false)}
          onCreated={handleLocationCreated}
        />
      )}

      {pendingContainer && (
        <ContainerNameModal
          gardenId={garden.id}
          point={pendingContainer}
          onClose={() => setPendingContainer(null)}
          onCreated={handleLocationCreated}
        />
      )}

      {pendingPlant && (
        <AddPlantOnMapModal
          location={pendingPlant.location}
          polygon={pendingPlant.polygon}
          initialPosition={pendingPlant.position}
          onClose={() => setPendingPlant(null)}
          onCreated={handlePlantCreated}
        />
      )}
    </div>
  );
}
